import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle, Paperclip } from 'lucide-react';
import { fadeInUp } from '../constants/animations';
import { buildApiUrl } from '../utils/api';
import './Careers.css';


const SALES_QUESTIONS = [
  'Why do you want to sell for Versa Launch?',
  'Describe your sales experience. What have you sold and how did you close deals?',
  'Walk us through how you would pitch Versa Launch to a local business owner.',
  'What does your availability look like and how many hours per week can you dedicate?'
];

const COMMISSION_QUESTION = 'Are you comfortable with a commission-only + recurring pay structure?';

const JOBS = [
  {
    id: 'sales-rep',
    status: 'open',
    title: 'Sales Representative',
    location: 'Remote',
    tags: ['Remote', 'Commission + Recurring', 'Full-Time / Part-Time'],
    goldTag: null,
    description:
      'Join the Versa Launch sales team and help local businesses grow. You\'ll pitch our all-in-one platform - websites, AI phone assistant, SEO, CRM, and more - to service business owners who need it most. We give you the leads, the tools, and the support. You bring the hustle.',
    perks: [
      'Warm leads provided - no cold list building required',
      'Commission on every deal + recurring monthly income',
      'Business phone number and CRM access from day one',
      'Flexible schedule - work from anywhere',
      'Ongoing support from our fulfillment and retention team',
      'Real upside with no earnings ceiling'
    ],
    payQuestion: COMMISSION_QUESTION,
    questions: SALES_QUESTIONS
  },
  {
    id: 'event-coordinator',
    status: 'open',
    title: 'Event & Popup Coordinator',
    location: 'Jacksonville, FL',
    tags: ['Jacksonville, FL', 'Commission + Recurring', 'Part-Time / Flexible'],
    goldTag: 'New',
    description:
      'Get Versa Launch in front of local business owners in person. You\'ll find and book the events worth showing up to - popups, expos, farmers markets, chamber mixers, trade shows, and business meetups - handle the applications and vendor fees, and make sure our table is set up and staffed. If you know your local business scene and enjoy working a room, this is your role.',
    perks: [
      'Own the local event calendar end to end',
      'Commission on every deal that traces back to an event you booked',
      'Booth materials, banners, and handouts provided',
      'Event fees and approved expenses covered',
      'Flexible, mostly evenings and weekends around your schedule',
      'Work directly with the founding team'
    ],
    payQuestion: COMMISSION_QUESTION,
    questions: [
      'What local events, popups, or markets would you book us into first, and why those?',
      'Tell us about a time you organized or staffed an event or booth. What did you handle?',
      'How would you start a conversation with a business owner walking past our table?',
      'What does your availability look like, especially evenings and weekends?'
    ]
  },
  {
    id: 'marketing',
    status: 'open',
    title: 'Marketing Specialist',
    location: 'Remote',
    tags: ['Remote', 'Performance-Based', 'Part-Time / Full-Time'],
    goldTag: 'New',
    description:
      'Own how Versa Launch shows up online. You\'ll run our social channels, write and schedule content, build simple landing pages and email campaigns, and keep the top of our funnel full. We are looking for someone who can both come up with the idea and actually ship it - not just plan it.',
    perks: [
      'Full creative ownership of our channels',
      'Direct line to the founders - no approval maze',
      'Access to our full platform, CRM, and analytics stack',
      'Your work is measured by leads generated, not hours logged',
      'Remote and flexible',
      'Room to grow into leading the marketing function'
    ],
    payQuestion: 'Are you comfortable with pay tied to performance and results?',
    questions: [
      'Which channels have you actually driven results on, and what were the numbers?',
      'Walk us through a campaign you ran start to finish - the idea, what you shipped, and what it produced.',
      'How would you get local service businesses to notice Versa Launch in the first 90 days?',
      'Which tools do you use to create, schedule, and measure your work?'
    ]
  },
  {
    id: 'software-dev',
    status: 'limited',
    title: 'Software Developer',
    location: 'Remote',
    tags: ['Remote', 'React + Node', 'Contract to Full-Time'],
    goldTag: null,
    description:
      'Build the platform our clients run their businesses on. You\'ll work across our React front end and Node/Express API on real, shipped features - client dashboards, the AI phone assistant, our site grader, booking, and billing. Small team, no ceremony, your code goes live fast.',
    perks: [
      'Ship to production in your first weeks, not your first quarter',
      'Modern stack - React, Node/Express, MySQL, third-party APIs',
      'Real ownership of features end to end',
      'Remote and async-friendly',
      'Direct access to the founders and to customers',
      'Contract start with a clear path to full-time'
    ],
    payQuestion: 'Are you comfortable starting on a contract basis with a path to full-time?',
    questions: [
      'Tell us about something you built and shipped end to end. What was your part in it?',
      'How comfortable are you across the stack - React on the front end and Node/Express APIs on the back?',
      'Share a link to your GitHub, portfolio, or anything you have shipped that we can look at.',
      'What does your availability look like and how many hours per week can you dedicate?'
    ]
  }
];

const MAX_BYTES = 15 * 1024 * 1024;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HIRING_INBOX = 'it@versalaunch.com';

// Formats as the applicant types: 5551234567 -> (555) 123-4567. Parentheses
// only appear once there are enough digits to need them, so backspacing never
// gets stuck on a bracket the applicant did not type.
function formatPhoneInput(value) {
  const raw = value.replace(/\D/g, '').slice(0, 11);
  const digits = raw.length === 11 && raw.startsWith('1') ? raw.slice(1) : raw;
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

// readAsDataURL streams the file, unlike btoa(String.fromCharCode(...bytes)),
// which throws on any resume big enough to overflow the argument stack.
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('We could not read that file. Please try uploading it again.'));
    reader.readAsDataURL(file);
  });
}

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p className="careers-field-error" id={`${id}-error`}>
      <AlertCircle size={14} /> {message}
    </p>
  );
}

function ApplicationModal({ job, onClose }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [answers, setAnswers] = useState({});
  const [commissionOk, setCommissionOk] = useState('');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [resume, setResume] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const overlayRef = useRef(null);

  // iOS Safari ignores `overflow: hidden` on the body, so the page behind the
  // modal keeps scrolling under the applicant's finger. Pinning the body and
  // restoring the offset on close is the one approach that holds there.
  useEffect(() => {
    const { body } = document;
    const scrollY = window.scrollY;
    const previous = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow
    };

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    return () => {
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.width = previous.width;
      body.style.overflow = previous.overflow;
      window.scrollTo(0, scrollY);
    };
  }, []);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  // Clear a field's complaint the moment the applicant starts fixing it.
  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const focusField = (key) => {
    const el = document.getElementById(`app-${key}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (typeof el.focus === 'function') el.focus({ preventScroll: true });
  };

  const showErrors = (errors, formMessage = '') => {
    setFieldErrors(errors);
    setError(formMessage);
    const first = Object.keys(errors)[0];
    if (first) focusField(first);
  };

  const handleResumeChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Every document type is accepted - only the size is capped.
    if (file.size > MAX_BYTES) {
      setFieldErrors((prev) => ({ ...prev, resume: 'That file is too large. Please upload one under 15 MB.' }));
      return;
    }
    clearFieldError('resume');
    setResume(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const errors = {};
    if (!form.name.trim()) errors.name = 'Please enter your full name.';
    if (!form.email.trim()) {
      errors.email = 'Please enter your email address.';
    } else if (!EMAIL_RE.test(form.email.trim())) {
      errors.email = 'That email address does not look right. Please check it and try again.';
    }
    if (form.phone.trim() && form.phone.replace(/\D/g, '').length < 10) {
      errors.phone = 'Please enter a 10-digit phone number, or leave it blank.';
    }
    job.questions.forEach((q, i) => {
      if (!answers[q]?.trim()) errors[`q-${i}`] = 'Please answer this question.';
    });
    if (!commissionOk) errors.commission = 'Please pick Yes or No.';
    if (!ageConfirmed) errors.age = 'Please confirm you are over the age of 18.';

    if (Object.keys(errors).length) {
      showErrors(errors);
      return;
    }

    setFieldErrors({});
    setSubmitting(true);
    try {
      let resumeBase64 = null;
      let resumeFilename = null;
      let resumeMimeType = null;

      if (resume) {
        resumeBase64 = await fileToBase64(resume);
        resumeFilename = resume.name;
        resumeMimeType = resume.type || 'application/octet-stream';
      }

      const response = await fetch(buildApiUrl('/api/career-applications'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          position: job.title,
          answers: { ...answers, [job.payQuestion]: commissionOk, 'Over 18?': 'Yes' },
          resumeBase64,
          resumeFilename,
          resumeMimeType
        })
      });

      // A failing server can answer with an HTML error page, so never assume
      // the body is JSON - the applicant should not read a parser error.
      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok) {
        const message = data.error
          || `We could not send your application just now. Please try again in a moment, or email it to ${HIRING_INBOX}.`;
        if (data.field) {
          showErrors({ [data.field]: message });
        } else {
          setError(message);
        }
        return;
      }

      setSubmitted(true);
    } catch (err) {
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
      setError(
        offline
          ? 'You appear to be offline. Reconnect and submit again - your answers are still here.'
          : err.message || `Something went wrong on our end. Please try again, or email your resume to ${HIRING_INBOX}.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Rendered into <body>. The app's route-shell sets `will-change: transform`,
  // which makes it the containing block for position:fixed children - without
  // the portal the overlay anchors to the scrolled page instead of the
  // viewport, and lands off-screen for anyone who scrolled before applying.
  return createPortal(
    <div className="careers-modal-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <motion.div
        className="careers-modal"
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
      >
        <div className="careers-modal-head">
          <div>
            <h2>Apply - {job.title}</h2>
            <p>Versa Launch · {job.location || 'Remote'}</p>
          </div>
          <button type="button" className="careers-modal-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {submitted ? (
          <div className="careers-modal-success">
            <div className="careers-modal-success-icon">🎉</div>
            <h3>Application Submitted</h3>
            <p>
              Thanks for applying! We've sent a confirmation to <strong>{form.email}</strong>. Our team reviews
              applications within 1–3 business days and will reach out if it's a fit.
            </p>
          </div>
        ) : (
          <form className="careers-modal-body" onSubmit={handleSubmit} noValidate>
            <p className="careers-form-section-label">Your Info</p>
            <div className="careers-form-row">
              <div className={`careers-form-field${fieldErrors.name ? ' careers-form-field--error' : ''}`}>
                <label htmlFor="app-name">Full Name *</label>
                <input
                  id="app-name"
                  type="text"
                  placeholder="Jane Smith"
                  value={form.name}
                  onChange={(e) => { setForm((p) => ({ ...p, name: e.target.value })); clearFieldError('name'); }}
                  aria-invalid={Boolean(fieldErrors.name)}
                  aria-describedby={fieldErrors.name ? 'app-name-error' : undefined}
                />
                <FieldError id="app-name" message={fieldErrors.name} />
              </div>
              <div className={`careers-form-field${fieldErrors.email ? ' careers-form-field--error' : ''}`}>
                <label htmlFor="app-email">Email *</label>
                <input
                  id="app-email"
                  type="email"
                  placeholder="jane@email.com"
                  value={form.email}
                  onChange={(e) => { setForm((p) => ({ ...p, email: e.target.value })); clearFieldError('email'); }}
                  aria-invalid={Boolean(fieldErrors.email)}
                  aria-describedby={fieldErrors.email ? 'app-email-error' : undefined}
                />
                <FieldError id="app-email" message={fieldErrors.email} />
              </div>
            </div>
            <div className={`careers-form-field${fieldErrors.phone ? ' careers-form-field--error' : ''}`}>
              <label htmlFor="app-phone">Phone (optional)</label>
              <input
                id="app-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="(555) 000-0000"
                value={form.phone}
                onChange={(e) => { setForm((p) => ({ ...p, phone: formatPhoneInput(e.target.value) })); clearFieldError('phone'); }}
                aria-invalid={Boolean(fieldErrors.phone)}
                aria-describedby={fieldErrors.phone ? 'app-phone-error' : undefined}
              />
              <FieldError id="app-phone" message={fieldErrors.phone} />
            </div>

            <p className="careers-form-section-label">Application Questions</p>
            {job.questions.map((q, i) => (
              <div key={i} className={`careers-form-field${fieldErrors[`q-${i}`] ? ' careers-form-field--error' : ''}`}>
                <label htmlFor={`app-q-${i}`}>{q} *</label>
                <textarea
                  id={`app-q-${i}`}
                  rows={3}
                  placeholder="Your answer..."
                  value={answers[q] || ''}
                  onChange={(e) => { setAnswers((p) => ({ ...p, [q]: e.target.value })); clearFieldError(`q-${i}`); }}
                  aria-invalid={Boolean(fieldErrors[`q-${i}`])}
                  aria-describedby={fieldErrors[`q-${i}`] ? `app-q-${i}-error` : undefined}
                />
                <FieldError id={`app-q-${i}`} message={fieldErrors[`q-${i}`]} />
              </div>
            ))}

            <div className={`careers-form-field${fieldErrors.commission ? ' careers-form-field--error' : ''}`}>
              <label htmlFor="app-commission">{job.payQuestion} *</label>
              <select
                id="app-commission"
                className="careers-form-select"
                value={commissionOk}
                onChange={(e) => { setCommissionOk(e.target.value); clearFieldError('commission'); }}
                data-empty={commissionOk ? undefined : 'true'}
                aria-invalid={Boolean(fieldErrors.commission)}
                aria-describedby={fieldErrors.commission ? 'app-commission-error' : undefined}
              >
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              <FieldError id="app-commission" message={fieldErrors.commission} />
            </div>

            <div className={`careers-form-field${fieldErrors.age ? ' careers-form-field--error' : ''}`}>
              <label className="careers-age-checkbox" htmlFor="app-age">
                <input
                  id="app-age"
                  type="checkbox"
                  checked={ageConfirmed}
                  onChange={(e) => { setAgeConfirmed(e.target.checked); clearFieldError('age'); }}
                  aria-invalid={Boolean(fieldErrors.age)}
                  aria-describedby={fieldErrors.age ? 'app-age-error' : undefined}
                />
                <span>I confirm I am over the age of 18 *</span>
              </label>
              <FieldError id="app-age" message={fieldErrors.age} />
            </div>

            <p className="careers-form-section-label">Resume</p>
            <div className={`careers-form-field${fieldErrors.resume ? ' careers-form-field--error' : ''}`}>
              <div className="careers-resume-upload">
                <input
                  id="app-resume"
                  type="file"
                  onChange={handleResumeChange}
                  aria-label="Upload your resume (any document type, max 15 MB)"
                  aria-invalid={Boolean(fieldErrors.resume)}
                  aria-describedby={fieldErrors.resume ? 'app-resume-error' : undefined}
                />
                <div className="careers-resume-upload-icon"><Paperclip size={22} strokeWidth={1.8} /></div>
                {resume ? (
                  <p><span className="careers-resume-upload-name">✓ {resume.name}</span></p>
                ) : (
                  <p><strong>Click to upload</strong> or drag & drop<br />PDF, Word, Pages, images - any file · Max 15 MB</p>
                )}
              </div>
              <FieldError id="app-resume" message={fieldErrors.resume} />
            </div>

            {(error || Object.keys(fieldErrors).length > 0) && (
              <div className="careers-form-error">
                <AlertCircle size={16} />
                {error || 'Please fix the highlighted fields above, then submit again.'}
              </div>
            )}

            <button type="submit" className="careers-submit-btn" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit Application'}
            </button>
            <p className="careers-submit-disclaimer">
              By submitting you agree that Versa Launch may contact you about this application via email or phone.
            </p>
          </form>
        )}
      </motion.div>
    </div>,
    document.body
  );
}

// 'limited' still takes applications - it only signals that we are hiring
// selectively for that role.
const STATUS_LABELS = { open: 'Open', limited: 'Limited', closed: 'Closed' };

function JobCard({ job, onApply }) {
  const isAccepting = job.status !== 'closed';
  return (
    <motion.div
      className={`careers-job-card${isAccepting ? '' : ' careers-job-card--closed'}`}
      variants={fadeInUp}
    >
      <div className="careers-job-card-header">
        <h3>{job.title}</h3>
        <span className={`careers-job-pill careers-job-pill--${job.status}`}>
          <span className="careers-job-pill-dot" />
          {STATUS_LABELS[job.status] || 'Open'}
        </span>
      </div>

      <div className="careers-job-meta">
        {job.goldTag && <span className="careers-job-tag careers-job-tag--gold">{job.goldTag}</span>}
        {job.tags.map((t) => (
          <span key={t} className="careers-job-tag">{t}</span>
        ))}
      </div>

      <p className="careers-job-desc">{job.description}</p>

      {job.perks.length > 0 && (
        <ul className="careers-job-perks">
          {job.perks.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}

      <button
        type="button"
        className={`careers-job-cta careers-job-cta--${job.status}`}
        onClick={() => isAccepting && onApply(job)}
        disabled={!isAccepting}
      >
        {isAccepting ? 'Apply Now' : 'Applications Closed'}
      </button>
    </motion.div>
  );
}

function Careers() {
  const [activeJob, setActiveJob] = useState(null);

  return (
    <main className="careers-page">
      {/* Hero */}
      <motion.section
        className="careers-hero"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.25 }}
      >
        <div className="container">
          <motion.span className="careers-kicker" variants={fadeInUp} custom={0}>Join the Team</motion.span>
          <motion.h1 variants={fadeInUp} custom={1}>
            Help businesses <span className="careers-gold-text">grow</span>.<br />
            Build your income doing it.
          </motion.h1>
          <motion.p variants={fadeInUp} custom={2}>
            Versa Launch is building the go-to growth platform for local service businesses. We're
            looking for driven closers who want real upside - not a salary ceiling.
          </motion.p>
        </div>
      </motion.section>

      {/* Open Positions */}
      <motion.section
        className="careers-jobs"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <div className="container">
          <motion.div className="careers-jobs-grid" variants={fadeInUp} custom={0}>
            {JOBS.map((job) => (
              <JobCard key={job.id} job={job} onApply={setActiveJob} />
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Application Modal */}
      <AnimatePresence>
        {activeJob && (
          <ApplicationModal job={activeJob} onClose={() => setActiveJob(null)} />
        )}
      </AnimatePresence>
    </main>
  );
}

export default Careers;
